import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, ExternalLink, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  Status,
  toast,
  type Column,
} from '@elbklar/ui';

import { ZeilenMenue } from '@/components/ZeilenMenue';
import { radarApi } from '@/lib/api';
import type { RadarActor, RadarActorStatus } from '@/types';
import { ACTOR_TYPE_TEXT } from './radarText';
import { ActorDialog } from './ActorDialog';

/**
 * Die Akteure — wer Programme ausschreibt. Vorgeschlagene stehen oben und
 * wollen bestätigt oder verworfen werden; das ist die einzige Entscheidung,
 * die hier regelmäßig ansteht.
 */
export function AkteureTab() {
  const queryClient = useQueryClient();
  const [dialogOffen, setDialogOffen] = useState(false);
  const [inBearbeitung, setInBearbeitung] = useState<RadarActor | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<RadarActor | null>(null);

  const akteure = useQuery({
    queryKey: ['radar', 'actors'],
    queryFn: () => radarApi.actors(),
  });

  useEffect(() => {
    if (akteure.isError) toast.error('Akteure konnten nicht geladen werden');
  }, [akteure.isError]);

  const aktualisieren = () => {
    void queryClient.invalidateQueries({ queryKey: ['radar'] });
  };

  const statusSetzen = useMutation({
    mutationFn: ({ id, status }: { id: string; status: RadarActorStatus }) => radarApi.updateActor(id, { status }),
    onSuccess: (_, { status }) => {
      toast.success(status === 'aktiv' ? 'Akteur bestätigt' : 'Akteur verworfen');
      aktualisieren();
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  // Der Fehler erscheint im Bestätigungsdialog, der dafür offen bleibt.
  const loeschen = useMutation({
    mutationFn: (id: string) => radarApi.removeActor(id),
    onSuccess: () => {
      toast.info('Akteur entfernt');
      aktualisieren();
    },
  });

  const oeffneDialog = (akteur: RadarActor | null) => {
    setInBearbeitung(akteur);
    setDialogOffen(true);
  };

  const liste = akteure.data ?? [];
  const vorgeschlagen = liste.filter((a) => a.status === 'vorgeschlagen').length;

  const spalten: Column<RadarActor>[] = [
    {
      key: 'name',
      header: 'Name',
      strong: true,
      mobil: 'titel',
      render: (a) => (
        <div className="flex items-center gap-2">
          <Building2 size={14} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <div>{a.name}</div>
            {/* Nur http(s) als Link: Die API prüft das Schema nicht, und ein
                `javascript:` im href würde React 18 mitrendern. */}
            {a.url && /^https?:\/\//i.test(a.url) && (
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 t-small font-normal text-muted-foreground hover:text-link hover:underline"
              >
                {a.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                <ExternalLink size={10} strokeWidth={1.75} aria-hidden />
              </a>
            )}
          </div>
        </div>
      ),
      renderMobil: (a) => a.name,
    },
    { key: 'type', header: 'Typ', mobil: 'unter', render: (a) => ACTOR_TYPE_TEXT[a.type] ?? a.type },
    { key: 'region', header: 'Region', mobil: 'unter', render: (a) => a.region ?? '–' },
    {
      key: 'program_count',
      header: 'Programme',
      align: 'end',
      mono: true,
      mobil: 'wert',
      render: (a) => a.program_count,
      // Am Handy fällt die Knopfspalte weg; Bestätigen, Verwerfen und Entfernen
      // stehen dann hinter „…“ an der Zahl (die Zeile selbst öffnet Bearbeiten).
      renderMobil: (a) => (
        <span className="flex items-center gap-1">
          <span className="tabular">{a.program_count}</span>
          <ZeilenMenue
            label={`Aktionen für ${a.name}`}
            aktionen={[
              ...(a.status === 'vorgeschlagen'
                ? [
                    { label: 'Bestätigen', icon: Check, onSelect: () => statusSetzen.mutate({ id: a.id, status: 'aktiv' as RadarActorStatus }) },
                    { label: 'Verwerfen', icon: X, onSelect: () => statusSetzen.mutate({ id: a.id, status: 'inaktiv' as RadarActorStatus }) },
                  ]
                : []),
              { label: 'Bearbeiten', icon: Pencil, onSelect: () => oeffneDialog(a) },
              { label: 'Entfernen', icon: Trash2, gefahr: true, onSelect: () => setZuLoeschen(a) },
            ]}
          />
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      mobil: 'status',
      render: (a) => <Status status={`akteur_${a.status}`} variant="plain" />,
    },
    {
      key: 'aktionen',
      header: '',
      align: 'end',
      render: (a) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {a.status === 'vorgeschlagen' && (
            <>
              <Button
                size="sm"
                loading={statusSetzen.isPending && statusSetzen.variables?.id === a.id}
                onClick={() => statusSetzen.mutate({ id: a.id, status: 'aktiv' })}
              >
                Bestätigen
              </Button>
              <Button size="sm" variant="ghost" onClick={() => statusSetzen.mutate({ id: a.id, status: 'inaktiv' })}>
                Verwerfen
              </Button>
            </>
          )}
          <IconButton icon={Pencil} label="Bearbeiten" variant="ghost" size="sm" onClick={() => oeffneDialog(a)} />
          <IconButton icon={Trash2} label="Entfernen" variant="ghost" size="sm" onClick={() => setZuLoeschen(a)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground">
          {liste.length} Akteure
          {vorgeschlagen > 0 && ` · ${vorgeschlagen} zu prüfen`}
        </p>
        <Button variant="primary" size="sm" icon={Plus} onClick={() => oeffneDialog(null)}>
          Akteur anlegen
        </Button>
      </div>

      <DataTable
        columns={spalten}
        rows={liste}
        loading={akteure.isLoading}
        onRowClick={(a) => oeffneDialog(a)}
        label="Akteure"
        empty={
          <EmptyState
            icon={Building2}
            title="Noch keine Akteure"
            description="Förderbanken, Kammern und Netzwerke, deren Programme das Radar im Blick behält, erscheinen hier."
          />
        }
      />

      <ActorDialog
        offen={dialogOffen}
        onSchliessen={() => setDialogOffen(false)}
        onGespeichert={aktualisieren}
        akteur={inBearbeitung}
      />

      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Akteur entfernen"
        description={zuLoeschen ? `„${zuLoeschen.name}“ wird aus dem Radar entfernt.` : undefined}
        confirmLabel="Akteur entfernen"
        tone="danger"
        onConfirm={() => (zuLoeschen ? loeschen.mutateAsync(zuLoeschen.id) : undefined)}
      />
    </div>
  );
}
